import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AuthError,
  changePassword as changeUserPassword,
  getAuthStatus,
  login as loginUser,
  logout as logoutUser,
  type AuthUser,
} from '../storage/authStore';

type AuthContextValue = {
  ready: boolean;
  user: AuthUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    getAuthStatus()
      .then((status) => {
        if (cancelled) return;
        setUser(status.user);
      })
      .catch((error) => {
        console.error('Failed to restore session', error);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const next = await loginUser(username, password);
    setUser(next);
    navigate('/schedule', { replace: true });
  }, [navigate]);

  const logout = useCallback(async () => {
    await logoutUser();
    setUser(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!user) throw new AuthError('authError');
      await changeUserPassword(user.username, currentPassword, newPassword);
    },
    [user],
  );

  const value = useMemo(
    () => ({ ready, user, login, logout, changePassword }),
    [ready, user, login, logout, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const isAuthError = (error: unknown): error is AuthError =>
  error instanceof AuthError;
